import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from '../../../hooks/useInView';

export default function ContactSection() {
  const { t } = useTranslation();
  const [ref, isInView] = useInView({ threshold: 0.1 });

  return (
    <section id="contacts" ref={ref} className="relative py-20 lg:py-28 overflow-hidden bg-slate-900">
      {/* Decorative blobs */}
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-sky-600/20 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 pointer-events-none"></div>
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-cyan-500/15 rounded-full blur-3xl translate-x-1/3 translate-y-1/3 pointer-events-none"></div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Text */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-cyan-400 text-sm font-semibold tracking-widest uppercase mb-4">
              {t('contact.label')}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
              {t('contact.title')}
            </h2>
            <p className="text-slate-300 text-lg mb-8 leading-relaxed">
              {t('contact.subtitle')}
            </p>

            {/* Info blocks */}
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-sky-500/25 border border-sky-400/40 shrink-0">
                  <i className="ri-map-pin-line text-cyan-400 text-lg"></i>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">{t('footer.address')}</p>
                  <p className="text-slate-400 text-xs mt-0.5">3 хв від метро «Площа Українських Героїв»</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-sky-500/25 border border-sky-400/40 shrink-0">
                  <i className="ri-time-line text-cyan-400 text-lg"></i>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">{t('footer.workingHours')}</p>
                  <p className="text-slate-400 text-xs mt-0.5">{t('footer.workingSaturday')}</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: CTA card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-8 shadow-2xl">
              <h3 className="text-white font-bold text-xl mb-2">{t('contact.cardTitle')}</h3>
              <p className="text-slate-300 text-sm mb-7">{t('contact.cardDesc')}</p>

              <div className="space-y-3">
                <a
                  href="tel:+380957403000"
                  className="group flex items-center justify-between w-full px-6 py-4 rounded-xl bg-gradient-to-r from-sky-500 to-cyan-500 text-white font-semibold shadow-lg shadow-sky-500/30 hover:shadow-sky-500/50 hover:scale-[1.02] transition-all duration-300 whitespace-nowrap"
                >
                  <span className="flex items-center gap-3">
                    <i className="ri-phone-line text-xl"></i>
                    <span>095 740 30 00</span>
                  </span>
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
                </a>
                <a
                  href="tel:+380937403000"
                  className="group flex items-center justify-between w-full px-6 py-4 rounded-xl bg-white/10 border border-white/25 text-white font-semibold hover:bg-white/20 hover:border-white/40 transition-all duration-300 whitespace-nowrap"
                >
                  <span className="flex items-center gap-3">
                    <i className="ri-phone-line text-xl text-cyan-400"></i>
                    <span>093 740 30 00</span>
                  </span>
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300 text-slate-300"></i>
                </a>

                <div className="flex gap-3 pt-1">
                  <a
                    href="https://t.me/goodfix_service"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="group flex items-center justify-center gap-2.5 flex-1 px-5 py-3.5 rounded-xl bg-[#229ED9]/20 border border-[#229ED9]/40 text-white font-semibold hover:bg-[#229ED9]/35 hover:border-[#229ED9]/60 hover:scale-[1.02] transition-all duration-300 whitespace-nowrap"
                  >
                    <i className="ri-telegram-line text-xl text-[#229ED9]"></i>
                    <span className="text-sm">Telegram</span>
                  </a>
                  <a
                    href="https://www.instagram.com/goodfix.com.ua/"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="group flex items-center justify-center gap-2.5 flex-1 px-5 py-3.5 rounded-xl bg-pink-500/20 border border-pink-400/40 text-white font-semibold hover:bg-pink-500/35 hover:border-pink-400/60 hover:scale-[1.02] transition-all duration-300 whitespace-nowrap"
                  >
                    <i className="ri-instagram-line text-xl text-pink-400"></i>
                    <span className="text-sm">Instagram</span>
                  </a>
                </div>
              </div>

              <div className="mt-5 pt-5 border-t border-white/15 flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shrink-0"></div>
                <p className="text-slate-300 text-sm">{t('contact.freeNote')}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
